import { PaginateType, type TPaginationResponse } from '@shared/types';
import type {
  VacancyTemplate,
  Vacancies,
  GetVacanciesRequest,
  CreateVacancyRequest,
  Vacancy,
  EditVacancyRequest,
  UpdateVacancyResponsibleRequest,
  VacancyStatus,
  GetVacancyStatusesHistoryRequest,
  VacancyStatusesHistory,
  UpdateVacancyStatusRequest,
  GetVacanciesTemplatesResponse,
  GetVacanciesTemplatesRequest,
  CreateVacanciesTemplateRequest,
  DetailVacanciesTemplate,
  UpdateVacanciesTemplateRequest,
  UpdateVacancyTemplateActivityRequest,
  VacancyTariff,
  VacancyTariffChangeLog,
  GetVacancyCandidatesRequest,
  CreateVacancyCopyResponse,
  CandidateProposedVacancies,
  GenerateVacancyResponse,
  GenerateVacancyRequest,
} from './types';
import type { Candidate } from '@entities/candidates';
import { BaseApi } from '@shared/config';

class VacancyApi extends BaseApi {
  getVacancies = (
    params: GetVacanciesRequest
  ): Promise<TPaginationResponse<Vacancies>> => {
    return this.instance.get(`vacancies`, { params });
  };

  /**
   * GET /api/candidates/{id}/vacancies.
   * Backend already scopes proposed vacancies; do not add client id filters.
   */
  getCandidateVacancies = (
    candidateId: string,
    params: GetVacanciesRequest
  ): Promise<TPaginationResponse<CandidateProposedVacancies>> => {
    return this.instance.get(`candidates/${candidateId}/vacancies`, {
      params,
    });
  };

  getVacancy = ({ id }: { id: number }): Promise<Vacancy> => {
    return this.instance.get(`vacancies/${id}`);
  };

  getVacancyCandidates(
    vacancyId: number,
    params: GetVacancyCandidatesRequest & { paginate: PaginateType.NONE }
  ): Promise<Candidate[]>;
  getVacancyCandidates(
    vacancyId: number,
    params: GetVacancyCandidatesRequest
  ): Promise<TPaginationResponse<Candidate[]>>;
  getVacancyCandidates(
    vacancyId: number,
    params: GetVacancyCandidatesRequest
  ): Promise<Candidate[] | TPaginationResponse<Candidate[]>> {
    return this.instance.get(`vacancies/${vacancyId}/candidates`, { params });
  }

  createVacancy = (vacancy: CreateVacancyRequest): Promise<Vacancy> => {
    return this.instance.post(`vacancies`, vacancy);
  };

  editVacancy = (
    vacancy: EditVacancyRequest,
    id?: string
  ): Promise<Vacancy> => {
    return this.instance.put(`vacancies/${id}`, vacancy);
  };

  deleteVacancy = (vacancyIds: number[]): Promise<void> => {
    return this.instance.delete(`vacancies`, {
      data: {
        vacancyIds,
      },
    });
  };

  updateVacansyResponsible = (
    id: string,
    params: UpdateVacancyResponsibleRequest
  ): Promise<Vacancy> => {
    return this.instance.put(`vacancies/${id}/change-responsible`, params);
  };

  updateVacancyStatus = (
    id: string,
    params: UpdateVacancyStatusRequest
  ): Promise<Vacancy> => {
    return this.instance.put(`vacancies/${id}/status`, params);
  };

  getVacancyStatuses = (): Promise<VacancyStatus[]> => {
    return this.instance.get('vacancies/statuses');
  };

  getVacancyTariffs = (): Promise<VacancyTariff[]> => {
    return this.instance.get(`vacancies/tariffs`);
  };

  getVacancyTariffsHistory = (
    vacancy_id: number
  ): Promise<VacancyTariffChangeLog[]> => {
    return this.instance.get(`vacancies/tariffs-history`, {
      params: { vacancy_id },
    });
  };

  getVacancyStatusesHistory = (
    id: number,
    params: GetVacancyStatusesHistoryRequest
  ): Promise<TPaginationResponse<VacancyStatusesHistory[]>> => {
    return this.instance.get(`vacancies/${id}/statuses-history`, { params });
  };

  getVacanciesTemplates = (
    params: GetVacanciesTemplatesRequest
  ): Promise<GetVacanciesTemplatesResponse> => {
    return this.instance.get(`vacancies/templates`, { params });
  };

  createVacanciesTemplate = (
    params: CreateVacanciesTemplateRequest
  ): Promise<VacancyTemplate> => {
    return this.instance.post(`vacancies/templates`, params);
  };

  getVacanciesTemplate = <T>(id: number): Promise<T> => {
    return this.instance.get(`vacancies/templates/${id}`);
  };

  updateVacanciesTemplate = (
    id: number,
    params: UpdateVacanciesTemplateRequest
  ): Promise<DetailVacanciesTemplate> => {
    return this.instance.put(`vacancies/templates/${id}`, params);
  };

  deleteVacanciesTemplate = (ids: number[]): Promise<void> => {
    return this.instance.delete(`vacancies/templates`, {
      data: {
        ids,
      },
    });
  };

  updateVacancyTemplateActivity = (
    id: number,
    params: UpdateVacancyTemplateActivityRequest
  ): Promise<DetailVacanciesTemplate> => {
    return this.instance.put(`vacancies/templates/${id}/activity`, params);
  };

  exportVacancy = ({
    dateFrom,
    dateTo,
  }: {
    dateFrom: string | null;
    dateTo?: string | null;
  }) => {
    return this.instance.post(
      `vacancies/export`,
      {
        data: dateFrom &&
          dateTo && {
            dateFrom,
            dateTo,
          },
      },
      {
        responseType: 'blob',
      }
    );
  };

  createVacancyCopy = (id: number): Promise<CreateVacancyCopyResponse> => {
    return this.instance.post(`vacancies/${id}/copy`);
  };

  generateVacancy = (
    params: GenerateVacancyRequest
  ): Promise<GenerateVacancyResponse> => {
    return this.instance.post('vacancies/generate-vacancy', params);
  };
}

export const vacancyApi = new VacancyApi();
